import { Coordinates } from '../../entities';

/**
 * Dimensions of the source image in pixels.
 */
export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Size of a roi in pixels and, when scale metadata is available, in microns.
 */
export interface RoiSize {
  /**
   * The width of the roi in pixels
   */
  widthPx: number;
  /**
   * The height of the roi in pixels
   */
  heightPx: number;
  /**
   * The area of the roi in square pixels
   */
  areaPx: number;
  /**
   * The width of the roi in microns. Undefined without scale metadata
   */
  widthUm?: number;
  /**
   * The height of the roi in microns. Undefined without scale metadata
   */
  heightUm?: number;
  /**
   * The area of the roi in square microns. Undefined without scale metadata
   */
  areaUm2?: number;
}

/**
 * Domain service for computing the pixel and physical size of a roi.
 */
export class RoiSizeService {
  /**
   * Computes the size of a roi from its normalized coordinates.
   *
   * @remarks assumes square pixels: the vertical micron scale reuses the horizontal one
   *
   * @param coordinates - Normalized (0-1) roi coordinates
   * @param image - Source image dimensions in pixels
   * @param hspanMm - Physical horizontal span of the full image in millimeters
   * @returns The {@link RoiSize} in pixels, including microns when hspanMm is provided
   */
  public static computeRoiSize(coordinates: Coordinates, image: ImageDimensions, hspanMm?: number): RoiSize {
    const widthPx = Math.abs(coordinates.x1 - coordinates.x0) * image.width;
    const heightPx = Math.abs(coordinates.y1 - coordinates.y0) * image.height;

    const size: RoiSize = {
      widthPx,
      heightPx,
      areaPx: widthPx * heightPx,
    };

    if (!hspanMm || hspanMm <= 0 || image.width <= 0) return size;

    const umPerPx = (hspanMm * 1000) / image.width;

    size.widthUm = widthPx * umPerPx;
    size.heightUm = heightPx * umPerPx;
    size.areaUm2 = size.widthUm * size.heightUm;

    return size;
  }
}
